import { useState, useEffect } from 'react';

import DenounceStatusService from "../services/DenounceStatusService";
import DenounceService from "../services/DenounceService";
import VectorFocusService from "../services/VectorFocusService";

import LogUtils from "../utils/LogUtils";

const TAG = "useDashboardService";
const logUtils = new LogUtils();

export function useGetDenounceVectorFocusReport({ userId, agentId }) {
  const [isMounted, setIsMounted] = useState(false);
  const [isLoading, setIsLoading] = useState(null);
  
  const [denounces, setDenounces] = useState([]);
  const [totalNumDenounces, setTotalNumDenounces] = useState(null);
  const [totalNumOpenedDenounces, setTotalNumOpenedDenounces] = useState(null);
  const [totalNumInProgresDenounces, setTotalNumInProgresDenounces] = useState(null);
  const [totalNumFinishedDenounces, setTotalNumFinishedDenounces] = useState(null);
  
  const [userVectorFoci, setUserVectorFoci] = useState([]);
  const [totalNumFocus, setTotalNumFocus] = useState(null);
  const [totalNumOpenedFocus, setTotalNumOpenedFocus] = useState(null);
  const [totalNumInProgresFocus, setTotalNumInProgresFocus] = useState(null);
  const [totalNumFinishedFocus, setTotalNumFinishedFocus] = useState(null);

  useEffect(() => {
    setIsMounted(true);

    return () => {
      setIsMounted(false);
      setDenounces([]);
      setTotalNumDenounces(null);
      setTotalNumOpenedDenounces(null);
      setTotalNumInProgresDenounces(null);
      setTotalNumFinishedDenounces(null);
      setUserVectorFoci([]);
      setTotalNumFocus(null);
      setTotalNumOpenedFocus(null);
      setTotalNumInProgresFocus(null);
      setTotalNumFinishedFocus(null);
    };
  }, []);

  useEffect(() => {
    const denounceStatusService = new DenounceStatusService();

    async function getUserDenouncesAndVectorFoci({ userId, agentId }) {
      try {
        if (!isMounted || !userId || !agentId) return false;

        setIsLoading(true);
        const responseDenounceStatus = await denounceStatusService.getDenounceStatus();
        const denounceService = new DenounceService();
        const responseAgentDenounces = await denounceService.getAgentUserDenounces(agentId);
        const vectorFocusService = new VectorFocusService();
        const responseVectorFocus = await vectorFocusService.getUserVectorFocus(userId);
        setIsLoading(false);

        if (!isMounted || !responseDenounceStatus?.data || !responseVectorFocus?.data) return false;

        setAgentDenouncesByDenounceStatus({
          denounceStatus: responseDenounceStatus?.data,
          agentDenounces: responseAgentDenounces?.data
        });
        setVectorFocusByDenounceStatus({
          denounceStatus: responseDenounceStatus?.data,
          vectorFoci: responseVectorFocus?.data
        });

      } catch (err) {
        logUtils.logErrorMsg(TAG, "Failed to getUserDenouncesAndVectorFoci", err);
        setIsLoading(false);
      }
    }

    function setAgentDenouncesByDenounceStatus({denounceStatus, agentDenounces}) {
      if (!Array.isArray(denounceStatus) || !Array.isArray(agentDenounces)) return;

      const denounceStatusByType = {};
      const updatedDenounces = [];

      denounceStatus.forEach(denounceStatus => {
        denounceStatusByType[denounceStatus.name] = 0;
        agentDenounces.forEach(denounce => {
          if (denounce.denounce_status_id === denounceStatus.id) {
            denounceStatusByType[denounceStatus.name] += 1;
            denounce.denounce_status = denounceStatus;
            updatedDenounces.push(denounce);
          }
        });
      });

      setDenounces(updatedDenounces);
      setTotalNumDenounces(updatedDenounces.length);
      setTotalNumOpenedDenounces(denounceStatusByType[denounceStatusService.DENOUNCE_STATUS.OPENED.name]);
      setTotalNumInProgresDenounces(denounceStatusByType[denounceStatusService.DENOUNCE_STATUS.IN_PROGRESS.name]);
      setTotalNumFinishedDenounces(denounceStatusByType[denounceStatusService.DENOUNCE_STATUS.CLOSED.name]);
    }

    function setVectorFocusByDenounceStatus({denounceStatus, vectorFoci}) {
      if (!Array.isArray(denounceStatus) || !Array.isArray(vectorFoci)) return;

      const denounceStatusByType = {};
      const updatedVectorFoci = [];

      denounceStatus.forEach(denounceStatus => {
        denounceStatusByType[denounceStatus.name] = 0;
        vectorFoci.forEach(vectorFocus => {
          if (vectorFocus.denounce_status_id === denounceStatus.id) {
            denounceStatusByType[denounceStatus.name] += 1;
            vectorFocus.denounce_status = denounceStatus;
            updatedVectorFoci.push(vectorFocus);
          }
        });
      });

      setUserVectorFoci(updatedVectorFoci);
      setTotalNumFocus(updatedVectorFoci.length);
      setTotalNumOpenedFocus(denounceStatusByType[denounceStatusService.DENOUNCE_STATUS.OPENED.name]);
      setTotalNumInProgresFocus(denounceStatusByType[denounceStatusService.DENOUNCE_STATUS.IN_PROGRESS.name]);
      setTotalNumFinishedFocus(denounceStatusByType[denounceStatusService.DENOUNCE_STATUS.CLOSED.name]);
    }

    getUserDenouncesAndVectorFoci({ userId, agentId });

  }, [isMounted, userId, agentId]);

  return {
    isLoading,
    denounces,
    userVectorFoci,
    totalNumDenounces,
    totalNumOpenedDenounces,
    totalNumInProgresDenounces,
    totalNumFinishedDenounces,
    totalNumFocus,
    totalNumOpenedFocus,
    totalNumInProgresFocus,
    totalNumFinishedFocus
  };
}
